// src/components/notify/console/NotifyHistoryTable.js
// Dispatch history: filters (status / source / topic / search), pagination, live refresh,
// AI-originated sends highlighted, row → NotifyDispatchDrawer.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, HStack, Icon, Input, InputGroup, InputLeftElement,
    Select, Skeleton, Stack, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useColorModeValue,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdChevronLeft, MdChevronRight, MdRefresh, MdSearch } from 'react-icons/md';
import * as api from '../../../services/notifyApi';
import notifyStream from '../../../services/notifyStream';
import NotifyDispatchDrawer from './NotifyDispatchDrawer';
import { CATEGORY_META, fmtDateTime, priorityMeta, SOURCE_META, sourceMeta, statusMeta, timeAgo } from '../notifyFormat';

const PAGE = 20;
const rate = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : '—');

export default function NotifyHistoryTable({ presetStatus = '', refreshKey = 0, onChanged }) {
    const panel = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const hover = useColorModeValue('gray.50', 'whiteAlpha.50');
    const [filters, setFilters] = useState({ status: presetStatus, source: '', category: '', q: '' });
    const [debouncedQ, setDebouncedQ] = useState('');
    const [page, setPage] = useState(0);
    const [state, setState] = useState({ loading: true, rows: [], total: 0, error: null });
    const [openId, setOpenId] = useState(null);
    const isMounted = useRef(true);
    const abortRef = useRef(null);
    useEffect(() => () => { isMounted.current = false; abortRef.current?.abort(); }, []);
    useEffect(() => { setFilters(f => ({ ...f, status: presetStatus })); setPage(0); }, [presetStatus]);
    useEffect(() => { const t = setTimeout(() => setDebouncedQ(filters.q), 300); return () => clearTimeout(t); }, [filters.q]);

    const load = useCallback(async () => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setState(s => ({ ...s, loading: true, error: null }));
        try {
            const r = await api.listDispatches({ status: filters.status, source: filters.source, category: filters.category, q: debouncedQ, limit: PAGE, offset: page * PAGE }, ctrl.signal);
            if (isMounted.current && !ctrl.signal.aborted) setState({ loading: false, rows: r.rows, total: r.total, error: null });
        } catch (e) {
            if (e?.name !== 'AbortError' && isMounted.current) setState(s => ({ ...s, loading: false, error: e }));
        }
    }, [filters.status, filters.source, filters.category, debouncedQ, page]);

    useEffect(() => { load(); }, [load, refreshKey]);

    // Live: refresh when a dispatch I sent finishes; poll while anything is still in flight
    useEffect(() => {
        let t = null;
        const off = notifyStream.on('dispatch_update', () => { clearTimeout(t); t = setTimeout(load, 400); });
        return () => { off(); clearTimeout(t); };
    }, [load]);
    useEffect(() => {
        if (!state.rows.some(r => ['queued', 'processing'].includes(r.status))) return undefined;
        const t = setInterval(load, 4000);
        return () => clearInterval(t);
    }, [state.rows, load]);

    const setF = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(0); };
    const pages = Math.max(1, Math.ceil(state.total / PAGE));

    return (
        <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 3, md: 4 }}>
            <Flex gap={2} mb={3} flexWrap="wrap" align="center">
                <InputGroup size="sm" maxW="260px">
                    <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color={muted} /></InputLeftElement>
                    <Input placeholder="Search title, message, audience" value={filters.q} onChange={(e) => setF('q', e.target.value)} borderRadius="lg" aria-label="Search dispatches" />
                </InputGroup>
                <Select size="sm" maxW="150px" value={filters.status} onChange={(e) => setF('status', e.target.value)} aria-label="Status filter">
                    <option value="">All statuses</option>
                    {['sent', 'scheduled', 'queued', 'processing', 'partial', 'failed', 'cancelled'].map(s => <option key={s} value={s}>{statusMeta(s).label}</option>)}
                </Select>
                <Select size="sm" maxW="170px" value={filters.source} onChange={(e) => setF('source', e.target.value)} aria-label="Source filter">
                    <option value="">All sources</option>
                    {Object.entries(SOURCE_META).filter(([k]) => !['legacy_model', 'queue'].includes(k)).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </Select>
                <Select size="sm" maxW="180px" value={filters.category} onChange={(e) => setF('category', e.target.value)} aria-label="Topic filter">
                    <option value="">All topics</option>
                    {Object.entries(CATEGORY_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </Select>
                <Box flex="1" />
                <Tooltip label="Refresh" hasArrow><Button size="sm" variant="ghost" onClick={load} isLoading={state.loading} aria-label="Refresh history"><MdRefresh /></Button></Tooltip>
            </Flex>

            {state.error ? (
                <Alert status="error" borderRadius="lg"><AlertIcon /><AlertDescription flex="1">{state.error.message}</AlertDescription><Button size="sm" onClick={load}>Retry</Button></Alert>
            ) : state.loading && !state.rows.length ? (
                <Stack spacing={2}>{[0, 1, 2, 3, 4].map(i => <Skeleton key={i} h="44px" borderRadius="md" />)}</Stack>
            ) : !state.rows.length ? (
                <Text color={muted} fontSize="sm" py={8} textAlign="center">No notifications match these filters.</Text>
            ) : (
                <Box overflowX="auto">
                    <Table size="sm">
                        <Thead>
                            <Tr><Th>Notification</Th><Th>Source</Th><Th>Status</Th><Th isNumeric>People</Th><Th isNumeric>Delivered</Th><Th isNumeric>Read</Th><Th isNumeric>Ack</Th><Th>When</Th></Tr>
                        </Thead>
                        <Tbody>
                            {state.rows.map(r => {
                                const st = statusMeta(r.status);
                                const src = sourceMeta(r.source);
                                const pr = priorityMeta(r.priority);
                                return (
                                    <Tr key={r.id} _hover={{ bg: hover }} cursor="pointer" onClick={() => setOpenId(r.id)} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setOpenId(r.id); }}>
                                        <Td maxW="340px">
                                            <HStack spacing={1.5}>
                                                {r.priority !== 'normal' && <Box w="8px" h="8px" borderRadius="full" bg={`${pr.color}.400`} flexShrink={0} title={pr.label} />}
                                                <Text fontWeight="600" fontSize="sm" noOfLines={1}>{r.title}</Text>
                                            </HStack>
                                            <Text fontSize="xs" color={muted} noOfLines={1}>{r.audience_label}</Text>
                                        </Td>
                                        <Td><Badge colorScheme={src.ai ? 'purple' : 'gray'} variant={src.ai ? 'subtle' : 'outline'} fontSize="2xs">{src.ai && <Icon as={MdAutoAwesome} mr={0.5} />}{src.label}</Badge></Td>
                                        <Td><Badge colorScheme={st.color} fontSize="2xs">{st.label}</Badge></Td>
                                        <Td isNumeric>{r.recipient_count || '—'}</Td>
                                        <Td isNumeric>{r.status === 'scheduled' ? '—' : rate(r.delivered_count, r.recipient_count)}</Td>
                                        <Td isNumeric>{r.status === 'scheduled' ? '—' : rate(r.read_count, r.recipient_count)}</Td>
                                        <Td isNumeric>{r.requires_ack ? `${r.ack_count}/${r.recipient_count}` : '—'}</Td>
                                        <Td whiteSpace="nowrap" fontSize="xs">
                                            <Tooltip label={fmtDateTime(r.scheduled_for || r.created_at)} hasArrow>
                                                <span>{r.status === 'scheduled' ? `⏱ ${fmtDateTime(r.scheduled_for)}` : timeAgo(r.created_at)}</span>
                                            </Tooltip>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}

            {state.total > PAGE && (
                <Flex justify="space-between" align="center" mt={3}>
                    <Text fontSize="xs" color={muted}>{state.total} total · page {page + 1} of {pages}</Text>
                    <HStack>
                        <Button size="xs" leftIcon={<MdChevronLeft />} onClick={() => setPage(p => Math.max(0, p - 1))} isDisabled={page === 0}>Prev</Button>
                        <Button size="xs" rightIcon={<MdChevronRight />} onClick={() => setPage(p => Math.min(pages - 1, p + 1))} isDisabled={page >= pages - 1}>Next</Button>
                    </HStack>
                </Flex>
            )}

            {openId && <NotifyDispatchDrawer dispatchId={openId} onClose={() => setOpenId(null)} onChanged={() => { load(); onChanged?.(); }} />}
        </Box>
    );
}
